// Developer: Ankit Gupta
/**
 * DataPilot application shell.
 *
 * The chrome is the AAFAQ assistant's chrome, shared with Insight and Confy:
 * a fixed 72px icon rail carrying the mark, one button per section of the
 * menu (each opening a panel of its pages beside the rail) and the account at
 * the foot; a flat top bar holding search, the scope, the credit balance and
 * the controls; and a content column that ends in the same footer. The pieces
 * come from styles/theme.css and components/common/RailNav, so the three
 * shells cannot drift apart.
 */
import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Dropdown, Tooltip, type MenuProps } from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  ProjectOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  SyncOutlined,
  BugOutlined,
  SafetyOutlined,
  SettingOutlined,
  AuditOutlined,
  ToolOutlined,
  BankOutlined,
  BarChartOutlined,
  UserOutlined,
  LogoutOutlined,
  KeyOutlined,
  MailOutlined,
  FileTextOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  CalendarOutlined,
  CloudDownloadOutlined,
  QuestionCircleOutlined,
  FileExcelOutlined,
  PieChartOutlined,
  CloudSyncOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  WalletOutlined,
  MessageOutlined,
  InboxOutlined,
  FundOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuthHook';
import { useAccess, type Permission } from '@/auth/useAccess';
import { useAppStore } from '@/store';
import { useActiveScope } from '@/hooks/useActiveScope';
import { useActiveJobs } from '@/hooks/useActiveJobs';
import NotificationBell, { type WaitingItem } from '@/components/common/NotificationBell';
import CreditsPill from '@/components/common/CreditsPill';
import { setSideCollapsed, showsConversations, useSideCollapsed } from '@/pages/Chat/sideCollapsed';
import { TaskCenterDrawer } from '@/components/common/TaskCenterDrawer';
import Logo from '@/components/common/Logo';
import { RhinoIcon } from '@/components/common/RhinoIcon';
import CommandPalette from '@/components/common/CommandPalette';
import ShortcutModal from '@/components/common/ShortcutModal';
import ThemeToggle from '@/components/common/ThemeToggle';
import LanguageToggle from '@/components/common/LanguageToggle';
import type { NavEntry } from '@/components/common/SideNav';
import { RailNav, type RailItem } from '@/components/common/RailNav';
import { getBalance } from '@/pages/Credits/api';
import { useT } from '@/i18n';
import { useIsRtl } from '@/hooks/useDirection';
import { OrionSparkle } from '@/pages/Chat/OrionIcon';
import { OrionDock } from '@/pages/Chat/Dock';
import { useWaitingApprovals } from '@/pages/Chat/Approvals';
import { useWaitingReviews } from '@/pages/Chat/PatchReview';
import { useMyProfile } from '@/pages/Profile';
import { useOrgMe } from '@/pages/Company/api';
import { orionApi } from '@/pages/Chat/api';
import { timesheetApi } from '@/pages/Timesheet/api';
import { MaintenanceBanner } from '@/components/common/MaintenanceBanner';
import { FeedbackButton, PulsePrompt } from '@/components/common/FeedbackButton';
import { OrionGreeting } from '@/pages/Chat/Greeting';
import { OrionSky } from '@/pages/Chat/OrionSky';

// ─── Menu ─────────────────────────────────────────────────────────────────────

// The menu is grouped by what a person comes to do: each group is one button
// on the rail, and the group's pages are the panel that button opens. Every
// page and every route is the one the product had before the rail of 12 Oct
// 2026; only the way they are reached is new.
const MIGRATE_NAV: NavEntry[] = [
  { key: 'projects', label: 'Projects', icon: <ProjectOutlined />, path: '/projects' },
  { key: 'submit', label: 'Submit Run', icon: <PlusCircleOutlined />, path: '/runs/submit' },
  { key: 'scheduled', label: 'Scheduled Runs', icon: <CalendarOutlined />, path: '/scheduled-runs' },
  { key: 'history', label: 'Run History', icon: <HistoryOutlined />, path: '/runs' },
  { key: 'fusion-export', label: 'Fusion Setup Export', icon: <CloudDownloadOutlined />, path: '/runs/fusion-export' },
  { key: 'reconciliation', label: 'Reconciliation', icon: <SyncOutlined />, path: '/reconciliation' },
  { key: 'entity-library', label: 'Entity Library', icon: <DeploymentUnitOutlined />, path: '/entity-library' },
  { key: 'templates', label: 'Template Library', icon: <FileExcelOutlined />, path: '/entity-library/templates' },
];

const CONFIGURE_NAV: NavEntry[] = [
  { key: 'confy', label: 'Confy', icon: <SettingOutlined />, path: '/confy' },
  { key: 'insight', label: 'Insight', icon: <PieChartOutlined />, path: '/insight' },
];

const DATA_NAV: NavEntry[] = [
  { key: 'data-sync', label: 'Data Sync', icon: <CloudSyncOutlined />, path: '/data-sync' },
  { key: 'reports', label: 'Reports', icon: <BarChartOutlined />, path: '/reports' },
];

const ACCOUNT_NAV: NavEntry[] = [
  { key: 'credits', label: 'Credits', icon: <ThunderboltOutlined />, path: '/credits' },
  // One entry, because /issues, /issues/new and /issues/mine all render the
  // same IssueList. Three menu items promised three destinations and delivered
  // one page headed "Issues Tracker"; the routes stay so existing links work.
  { key: 'issues', label: 'Issues', icon: <BugOutlined />, path: '/issues' },
  { key: 'company', label: 'Company', icon: <BankOutlined />, path: '/company' },
  // what the person sent from the top bar's feedback icon; the next two are for administrators
  { key: 'feedback', label: 'My feedback', icon: <MessageOutlined />, path: '/feedback' },
  { key: 'feedback-manage', label: 'Feedback', icon: <InboxOutlined />, path: '/feedback/manage' },
  { key: 'product-insights', label: 'Product Insights', icon: <FundOutlined />, path: '/feedback/insights' },
];

const ADMIN_NAV: NavEntry[] = [
  { key: 'fusion-extracts', label: 'Fusion Extracts', icon: <FileTextOutlined />, path: '/fusion-extracts' },
  { key: 'analytics', label: 'Usage & Analytics', icon: <BarChartOutlined />, path: '/manage/analytics' },
  { key: 'users', label: 'Users', icon: <TeamOutlined />, path: '/manage/users' },
  { key: 'organizations', label: 'Organizations', icon: <BankOutlined />, path: '/manage/organizations' },
  { key: 'subscriptions', label: 'Subscriptions', icon: <ThunderboltOutlined />, path: '/manage/subscriptions' },
  { key: 'earnings', label: 'Earnings', icon: <BarChartOutlined />, path: '/manage/earnings' },
  { key: 'config', label: 'Configuration', icon: <SettingOutlined />, path: '/manage/config' },
  { key: 'maintenance', label: 'Maintenance', icon: <ToolOutlined />, path: '/manage/maintenance' },
  { key: 'timesheet-access', label: 'My timesheet pilot', icon: <ClockCircleOutlined />, path: '/manage/timesheet-access' },
  { key: 'keystore', label: 'Key Store', icon: <KeyOutlined />, path: '/manage/keystore' },
  { key: 'mail', label: 'Mail Templates', icon: <MailOutlined />, path: '/manage/mail-templates' },
  { key: 'lookups', label: 'Lookups', icon: <UnorderedListOutlined />, path: '/manage/lookups' },
  { key: 'extract-validations', label: 'Extract Validation Mapping', icon: <LinkOutlined />, path: '/manage/extract-validations' },
  // Debug reads raw application logs and was sitting in the customer-facing
  // menu at the same weight as Users. The route still exists for anyone who
  // knows it; it is no longer advertised in a production build.
  ...(import.meta.env.DEV
    ? [{ key: 'debug', label: 'Debug', icon: <BugOutlined />, path: '/manage/debug' }]
    : []),
];

// What opens each entry (GET /access/me). An entry with no permission named here is for
// everyone who reached the shell; a section whose entries all close disappears from the rail.
const NEEDS: Record<string, Permission> = {
  projects: 'projects.view',
  submit: 'runs.use',
  scheduled: 'runs.use',
  history: 'runs.use',
  'fusion-export': 'runs.use',
  reconciliation: 'runs.use',
  'entity-library': 'library.view',
  templates: 'library.view',
  confy: 'confy.use',
  insight: 'insight.use',
  rhino: 'rhino.use',
  'data-sync': 'data.sync',
  reports: 'reports.view',
  credits: 'credits.view',
  issues: 'issues.use',
  company: 'org.view',
  // a company's administrators and the DataPilot team (who hold every permission)
  'feedback-manage': 'org.manage',
  'product-insights': 'org.manage',
};

/**
 * Whether a nav path is the page being viewed.
 *
 * The rules that are not just a prefix test: the dashboard is the only exact
 * match for '/', '/runs' must not light up for '/runs/submit' or the export
 * page which are their own entries, and the entity library must not light up
 * for the template library that lives beneath it.
 */
function isPathActive(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  if (path === '/runs') {
    return (
      pathname === '/runs' ||
      (pathname.startsWith('/runs/') &&
        !pathname.startsWith('/runs/submit') &&
        !pathname.startsWith('/runs/fusion-export'))
    );
  }
  if (path === '/feedback') return pathname === '/feedback';
  if (path === '/orion') {
    return pathname === '/orion' || /^\/orion\/(new|\d+)/.test(pathname);
  }
  if (path === '/entity-library') {
    return pathname === '/entity-library' || (pathname.startsWith('/entity-library/') && !pathname.startsWith('/entity-library/templates'));
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** What the Orion menu held last time, so it is drawn at once and corrected when the list arrives.
 *  Kept in this browser only; a menu entry still leads to a page that checks access itself. */
interface OrionMenuSeen { any: boolean; uses: boolean; acts: boolean; owns: boolean }
const ORION_MENU_KEY = 'datapilot.orion-menu';
function lastOrionMenu(): OrionMenuSeen | null {
  try {
    const v = JSON.parse(window.localStorage.getItem(ORION_MENU_KEY) ?? 'null') as OrionMenuSeen | null;
    return v && typeof v.any === 'boolean' ? v : null;
  } catch { return null; }
}
function keepOrionMenu(v: OrionMenuSeen) {
  try { window.localStorage.setItem(ORION_MENU_KEY, JSON.stringify(v)); } catch { /* storage is a convenience */ }
}

// ─────────────────────────────────────────────────────────────────────────────

export function MainLayout() {
  const { t } = useT('shell');
  const rtl = useIsRtl();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { can, access } = useAccess();
  const platformAdmin = can('platform.admin');
  // The menu constants above carry English for reading the code; what is shown
  // is looked up by key at render time, in the interface language.
  const label = (entries: NavEntry[]) => entries.map((e) => ({ ...e, label: t(`nav.${e.key}`, { defaultValue: e.label }) }));
  const open = (entries: NavEntry[]) => label(entries.filter((e) => !NEEDS[e.key] || can(NEEDS[e.key])));
  const instanceName = useAppStore((s) => s.instanceName);

  const [commandPaletteOpen, setCommandPaletteOpen] = React.useState(false);
  const [shortcutModalOpen, setShortcutModalOpen] = React.useState(false);
  const [taskCenterOpen, setTaskCenterOpen] = React.useState(false);
  const { activeCount, hasActive } = useActiveJobs();

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // on Orion's conversation screens the keys go to the conversation search, as the theme shows
        const search = showsConversations(window.location.pathname) ? document.querySelector<HTMLInputElement>('.chat-side:not(.is-hidden) .chat-side-q input') : null;
        if (search && search.offsetParent !== null) { search.focus(); search.select(); return; }
        setCommandPaletteOpen((o) => !o);
      } else if (
        e.key === '?' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)
      ) {
        e.preventDefault();
        setShortcutModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // The balance pill. Short-lived because submitting a run spends credits and a
  // stale pill contradicts what the user just did, and on the same key the
  // Credits page invalidates so redeeming a top-up updates the pill too.
  const { data: balance } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: () => getBalance(),
    staleTime: 15_000,
    retry: false,
  });

  const isActive = (path: string) => isPathActive(location.pathname, path);

  // Orion's entries carry what is waiting: steps to approve, patches to deploy or verify.
  const approvalsWaiting = useWaitingApprovals().data ?? [];
  const reviewsWaiting = useWaitingReviews().data ?? [];
  const waitingApprovals = approvalsWaiting.length + reviewsWaiting.length;
  // what the bell lists above the notifications: Orion steps and patches that wait for this person
  const bellWaiting: WaitingItem[] = [
    ...approvalsWaiting.slice(0, 6).map((a) => ({ key: `a${a.approval_id}`, title: t('bell.approval', { app: a.application }), body: a.thread_title || a.lead, link: `/orion/${a.thread_id}` })),
    ...reviewsWaiting.slice(0, 6).map((p) => ({ key: `r${p.id}`, title: t('bell.review', { app: p.application_name }), body: p.name, link: p.thread_id ? `/orion/${p.thread_id}` : '/orion/patches' })),
  ];
  const collapsed = useSideCollapsed();
  const onConversations = showsConversations(location.pathname);
  // what the person runs: Operations is for owners, and an owner sees who is asking for access
  const orionQuery = useQuery({ queryKey: ['orion-apps'], queryFn: () => orionApi.applications(), staleTime: 60_000, refetchInterval: 120_000, retry: false });
  const orionApps = orionQuery.data ?? [];
  // The list holds the applications the person has a role in or may ask for. Someone with none
  // sees no Orion at all; someone who may only ask sees the place to ask; approvals and patches
  // appear for people who can act on them, or who have something waiting. Until the list arrives
  // the menu is drawn as it was last time, so Orion does not appear seconds after the rest.
  const seen = orionQuery.data ? null : lastOrionMenu();
  const ownsAny = seen ? seen.owns : orionApps.some((a) => a.can_manage);
  const asking = orionApps.reduce((n, a) => n + (a.can_manage ? a.pending_requests : 0), 0);
  const usesAny = seen ? seen.uses : orionApps.some((a) => a.my_role);
  const actsAny = (seen ? seen.acts : orionApps.some((a) => a.my_role && a.my_role !== 'viewer')) || waitingApprovals > 0;
  const anyApps = seen ? seen.any : orionApps.length > 0;
  React.useEffect(() => {
    if (orionQuery.data) keepOrionMenu({ any: orionApps.length > 0, uses: usesAny, acts: orionApps.some((a) => a.my_role && a.my_role !== 'viewer'), owns: ownsAny });
  }, [orionQuery.data, orionApps, usesAny, ownsAny]);
  // an auditor of private conversations (often from an outside firm, with no application of their own)
  const auditor = useQuery({ queryKey: ['orion-audit-me'], queryFn: () => orionApi.auditMe(), staleTime: 300_000, retry: false }).data?.auditor ?? false;
  const auditNav: NavEntry[] = auditor ? [{ key: 'orion-audit', label: t('nav.orion-audit'), icon: <AuditOutlined />, path: '/orion/audit' }] : [];
  const orionNav: NavEntry[] = !anyApps ? auditNav : [
    ...(usesAny ? [{ key: 'orion-conversations', label: t('nav.orion-conversations'), icon: <OrionSparkle />, path: '/orion' }] : []),
    ...(actsAny ? [
      { key: 'orion-approvals', label: waitingApprovals > 0 ? t('nav.orion-approvals-count', { count: waitingApprovals }) : t('nav.orion-approvals'), icon: <CheckCircleOutlined />, path: '/orion/approvals' },
      { key: 'orion-patches', label: t('nav.orion-patches'), icon: <FileTextOutlined />, path: '/orion/patches' },
    ] : []),
    { key: 'orion-applications', label: asking > 0 ? t('nav.orion-applications-count', { count: asking }) : t('nav.orion-applications'), icon: <TeamOutlined />, path: '/orion/applications' },
    ...(ownsAny ? [{ key: 'orion-operations', label: t('nav.orion-operations'), icon: <BarChartOutlined />, path: '/orion/operations' }] : []),
    ...auditNav,
  ];

  // My timesheet is a pilot: the entry appears for the people on it
  const timesheetOpen = useQuery({ queryKey: ['timesheet-access'], queryFn: timesheetApi.access, staleTime: 300_000, retry: false }).data?.enabled ?? false;
  const timesheetNav: NavEntry[] = timesheetOpen ? [{ key: 'timesheet', label: 'My timesheet', icon: <ClockCircleOutlined />, path: '/timesheet' }] : [];

  const rhinoNav: NavEntry[] = [{ key: 'rhino', label: 'Rhino', icon: <RhinoIcon />, path: '/rhino' }];

  const myProfile = useMyProfile().data;
  const company = useOrgMe().data?.organization?.name;

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'who',
      label: (
        <div style={{ padding: '2px 0' }}>
          <div style={{ fontWeight: 600 }}>{myProfile?.display_name || user?.fullName || user?.username || t('user.fallbackName')}</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>{user?.email}</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {platformAdmin ? t('user.roleAdministrator') : access?.organization?.role === 'ADMIN' ? t('user.roleCompanyAdmin') : t('user.roleMember')}
            {company ? ` · ${company}` : ''}
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('user.profile') },
    { key: 'credits', icon: <ThunderboltOutlined />, label: t('user.credits') },
    { key: 'shortcuts', icon: <QuestionCircleOutlined />, label: t('user.shortcuts') },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('user.logout'), danger: true },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') logout();
    if (key === 'profile') navigate('/profile');
    if (key === 'credits') navigate('/credits');
    if (key === 'shortcuts') setShortcutModalOpen(true);
  };

  // Rhino and Orion are workbenches: they manage their own panes and need the full column.
  const isWorkbench = location.pathname.startsWith('/rhino') || location.pathname.startsWith('/orion');

  const { activeProjectId } = useActiveScope();
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const go = (path: string) => {
    setMenuOpen(false);
    if (path === '/insight' && activeProjectId) {
      navigate(`/insight?project=${activeProjectId}`);
      return;
    }
    if (path === '/confy' && activeProjectId) {
      navigate(`/confy?project=${activeProjectId}`);
      return;
    }
    navigate(path);
  };

  // One rail button per section, holding the entries the person's access opens; a section
  // with nothing open is not shown. A section with a single page is the page.
  const sections: { key: string; label: string; icon: React.ReactNode; entries: NavEntry[] }[] = [
    { key: 'migrate', label: t('rail.migrate'), icon: <DatabaseOutlined />, entries: open(MIGRATE_NAV) },
    { key: 'configure', label: t('rail.configure'), icon: <AppstoreOutlined />, entries: open(CONFIGURE_NAV) },
    { key: 'data', label: t('rail.data'), icon: <CloudSyncOutlined />, entries: open([...rhinoNav, ...DATA_NAV]) },
    { key: 'account', label: t('rail.account'), icon: <WalletOutlined />, entries: open([...timesheetNav, ...ACCOUNT_NAV]) },
  ];
  const rail: RailItem[] = [
    { key: 'home', label: t('rail.home'), icon: <DashboardOutlined />, path: '/' },
    ...(orionNav.length > 0
      ? [{ key: 'orion', label: 'Orion', icon: <OrionSparkle />, entries: can('orion.use') ? orionNav : auditNav }]
      : []),
    { key: 'd1', divider: true },
    ...sections.filter((s) => s.entries.length > 0),
    ...(platformAdmin
      ? [
          { key: 'd2', divider: true } as RailItem,
          { key: 'admin', label: t('rail.admin'), icon: <SafetyOutlined />, entries: label(ADMIN_NAV) },
        ]
      : []),
  ];

  return (
    <div className={`dp-app${menuOpen ? ' is-nav-open' : ''}`}>
      {/* Mobile scrim backdrop */}
      {menuOpen && (
        <div
          className="dp-nav-scrim"
          role="presentation"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* ── The rail ── */}
      <aside className="dp-sider" aria-label={t('rail.ariaMenu')}>
        <div className="dp-wordmark">
          <Logo collapsed size={34} darkTheme />
        </div>

        <RailNav items={rail} isActive={isActive} onNavigate={go} />

        <div className="dp-rail-foot">
          <button type="button" className="dp-rail-item" onClick={() => setShortcutModalOpen(true)}>
            <QuestionCircleOutlined />
            <span className="dp-rail-label">{t('rail.help')}</span>
          </button>
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement={rtl ? 'topRight' : 'topLeft'}
            trigger={['click']}
          >
            <button type="button" className="dp-rail-avatar" aria-label={t('rail.ariaAccount')}>
              <span className="dp-rail-avatar-face">{myProfile?.avatar ? <img src={myProfile.avatar} alt="" /> : <UserOutlined />}</span>
              <span className="dp-rail-avatar-on" aria-hidden="true" />
            </button>
          </Dropdown>
        </div>
      </aside>

      {/* ── Main column ── */}
      <div className="dp-main">
        <MaintenanceBanner />
        <header className={`dp-topbar${location.pathname.startsWith('/orion') ? ' has-sky' : ''}`}>
          {location.pathname.startsWith('/orion') && <OrionSky />}
          <button
            type="button"
            className="dp-hamburger"
            aria-label={menuOpen ? t('topbar.closeMenu') : t('topbar.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <MenuOutlined />
          </button>

          {onConversations && (
            <Tooltip title={collapsed ? t('topbar.showConversations') : t('topbar.hideConversations')}>
              <button type="button" className="dp-topbar-toggle" aria-pressed={!collapsed} aria-label={collapsed ? t('topbar.showConversations') : t('topbar.hideConversations')} onClick={() => setSideCollapsed(!collapsed)}>
                {collapsed ? <MenuUnfoldOutlined className="dp-flip-rtl" /> : <MenuFoldOutlined className="dp-flip-rtl" />}
              </button>
            </Tooltip>
          )}

          {/* Pushes the pills to the far end of the bar, which the search box used to fill. */}
          {/* while someone works in Orion, Orion greets them in the middle of the bar */}
          {location.pathname.startsWith('/orion') ? <OrionGreeting /> : <div style={{ flex: 1 }} />}

          {balance && <CreditsPill balance={balance} />}

          {/* Background Task & Execution Center */}
          {hasActive ? (
            <Tooltip title={t('topbar.activeJobsTip', { count: activeCount })}>
              <button type="button" className="dp-pill dp-pill-btn dp-pill-active-jobs" onClick={() => setTaskCenterOpen(true)}>
                <SyncOutlined spin className="dp-pill-icon" />
                <span className="dp-pill-label">{t('topbar.activeJobs')}</span>
                <span className="dp-pill-value">{t('topbar.running', { count: activeCount })}</span>
              </button>
            </Tooltip>
          ) : (
            <Tooltip title={t('topbar.idleTip')}>
              <button type="button" className="dp-pill dp-pill-btn" onClick={() => setTaskCenterOpen(true)}>
                <span className="dp-pill-dot" aria-hidden="true" />
                <span className="dp-pill-label">{t('topbar.tasks')}</span>
                <span className="dp-pill-value">{t('topbar.idle')}</span>
              </button>
            </Tooltip>
          )}

          <OrionDock />

          <LanguageToggle />

          <ThemeToggle />

          <NotificationBell waiting={bellWaiting} />
        </header>

        <PlanBanner />

        <main className={`dp-content${isWorkbench ? '' : ' is-page'}`} style={isWorkbench ? { padding: '4px 12px 12px' } : undefined}>
          <Outlet />

          {!isWorkbench && (
            <div className="dp-footer">
              <span>DataPilot</span>
              <span>{instanceName}</span>
            </div>
          )}
        </main>

        <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
        <ShortcutModal open={shortcutModalOpen} onClose={() => setShortcutModalOpen(false)} />
        <TaskCenterDrawer open={taskCenterOpen} onClose={() => setTaskCenterOpen(false)} />
        <FeedbackButton />
        <PulsePrompt />
      </div>
    </div>
  );
}

export default MainLayout;

/**
 * One line under the top bar when the company's plan needs attention: a trial in its last
 * week or run out, a payment that failed, a cancelled plan. Administrators are sent to the
 * Company page; members are told to ask one.
 */
function PlanBanner() {
  const { t } = useT('shell');
  const { access } = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const org = access?.organization;
  if (!org || access?.is_platform_admin || location.pathname.startsWith('/company')) return null;
  const manages = org.role === 'ADMIN';
  let text: string | null = null;
  let urgent = false;
  if (org.plan_status === 'TRIAL' && org.days_left != null && org.days_left <= 7) {
    const plan = org.plan_name ?? t('plan.yourPlan');
    text = org.days_left === 0 ? t('plan.trialEndsToday', { plan }) : t('plan.trialEndsIn', { plan, count: org.days_left });
  } else if (org.plan_status === 'EXPIRED') {
    text = t('plan.expired');
    urgent = true;
  } else if (org.plan_status === 'PAST_DUE') {
    text = t('plan.pastDue');
    urgent = true;
  } else if (org.plan_status === 'CANCELLED') {
    text = t('plan.cancelled');
    urgent = true;
  }
  if (!text) return null;
  return (
    <div className={`dp-plan-banner${urgent ? ' urgent' : ''}`} role="status">
      <span>{text}{manages ? '' : ` ${t('plan.askAdmin')}`}</span>
      {manages && <button type="button" className="dp-link-button" onClick={() => navigate('/company')}>{t('plan.choose')}</button>}
    </div>
  );
}
